import React from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  Loader,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

const CANONICAL_FIELDS = [
  { key: 'test_case_no', label: 'Test case ID', required: false },
  { key: 'test_description', label: 'Description / condition', required: true },
  { key: 'preconditions', label: 'Preconditions', required: false },
  { key: 'test_data', label: 'Test data', required: false },
  { key: 'test_steps', label: 'Test steps', required: true },
  { key: 'expected_result', label: 'Expected result', required: true },
  { key: 'execution_status', label: 'Execution status', required: false },
  { key: 'actual_result', label: 'Actual result', required: false },
  { key: 'category', label: 'Positive / negative category', required: false },
  { key: 'module', label: 'Module', required: false },
  { key: 'user_role', label: 'User role', required: false },
  { key: 'tab_flows', label: 'Screen / tab flow', required: false },
  { key: 'priority', label: 'Priority', required: false },
  { key: 'tags', label: 'Tags', required: false },
];

const REQUIRED_FIELDS = CANONICAL_FIELDS
  .filter((field) => field.required)
  .map((field) => field.key);

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeHeaderDescriptor = (value, index) => {
  if (typeof value === 'string' || typeof value === 'number') {
    const label = String(value).trim();
    return label ? { label, selector: label, key: `${label}-${index}` } : null;
  }
  if (!value || typeof value !== 'object') return null;
  const label = String(
    value.value
    ?? value.header
    ?? value.source_header
    ?? value.label
    ?? ''
  ).trim();
  const column = String(value.column || '').trim().toUpperCase();
  const selector = column || label;
  if (!label || !selector) return null;
  return {
    label,
    column,
    columnIndex: Number(value.column_index || value.columnIndex || 0) || null,
    selector,
    key: `${column || selector}-${index}`,
  };
};

const resolveSheetHeaders = (sheet) => {
  const candidates = [
    sheet?.headers,
    sheet?.header?.cells,
    sheet?.columns,
  ];
  for (const candidate of candidates) {
    const headers = toArray(candidate)
      .map(normalizeHeaderDescriptor)
      .filter(Boolean);
    if (headers.length > 0) return headers;
  }
  return [];
};

const resolveMappingSelector = (entry) => {
  if (typeof entry === 'string' || typeof entry === 'number') return String(entry).trim();
  if (!entry || typeof entry !== 'object') return '';
  return String(
    entry.column
    ?? entry.source_header
    ?? entry.header
    ?? entry.source
    ?? ''
  ).trim();
};

const resolveMappingConfidence = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const raw = Number(entry.confidence);
  if (!Number.isFinite(raw)) return null;
  return raw > 1 ? Math.max(0, Math.min(1, raw / 100)) : Math.max(0, Math.min(1, raw));
};

const resolveUsedRange = (usedRange) => {
  if (!usedRange) return '';
  if (typeof usedRange === 'string' || typeof usedRange === 'number') {
    return String(usedRange);
  }
  return String(usedRange.coordinate || usedRange.range || '').trim();
};

const resolveDetectedCaseCount = (payload) => Number(
  payload?.case_count
  ?? payload?.workbook?.case_count
  ?? payload?.workbook?.total_case_count
  ?? resolveCases(payload).length
) || 0;

const buildInitialMapping = (analysis) => {
  const next = {};
  toArray(analysis?.sheets).forEach((sheet) => {
    const sheetName = String(sheet?.name || sheet?.sheet_name || '').trim();
    if (!sheetName) return;
    const candidate = sheet?.effective_mapping || sheet?.suggested_mapping || {};
    const normalized = {};
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      Object.entries(candidate).forEach(([canonicalField, entry]) => {
        const selector = resolveMappingSelector(entry);
        if (selector) normalized[canonicalField] = selector;
      });
    }
    next[sheetName] = normalized;
  });
  return next;
};

const buildInitialSheetCategories = (analysis) => {
  const next = {};
  toArray(analysis?.sheets).forEach((sheet) => {
    const sheetName = String(sheet?.name || sheet?.sheet_name || '').trim();
    if (!sheetName) return;
    const category = String(sheet?.category || '').trim().toLowerCase();
    next[sheetName] = ['positive', 'negative'].includes(category)
      ? category
      : 'uncategorized';
  });
  return next;
};

const diagnosticMessage = (diagnostic) => {
  if (typeof diagnostic === 'string') return diagnostic;
  if (!diagnostic || typeof diagnostic !== 'object') return '';
  return String(
    diagnostic.message
    || diagnostic.detail
    || diagnostic.reason
    || diagnostic.code
    || ''
  ).trim();
};

const collectDiagnostics = (payload) => {
  const output = [];
  toArray(payload?.diagnostics).forEach((item) => {
    const message = diagnosticMessage(item);
    if (message) output.push(message);
  });
  toArray(payload?.sheets).forEach((sheet) => {
    toArray(sheet?.diagnostics).forEach((item) => {
      const message = diagnosticMessage(item);
      if (message) output.push(`${sheet?.name || 'Sheet'}: ${message}`);
    });
  });
  return Array.from(new Set(output));
};

const resolveCases = (payload) => {
  if (Array.isArray(payload?.cases)) return payload.cases;
  if (Array.isArray(payload?.test_cases)) return payload.test_cases;
  const grouped = payload?.groups || payload?.test_cases;
  if (grouped && typeof grouped === 'object') {
    return [
      ...toArray(grouped.positive),
      ...toArray(grouped.negative),
      ...toArray(grouped.uncategorized),
      ...toArray(grouped.all),
    ];
  }
  return [];
};

const caseStepsText = (testCase) => {
  const steps = testCase?.test_steps ?? testCase?.steps;
  if (Array.isArray(steps)) return steps.join('\n');
  return String(steps || '');
};

const caseExpectedResultText = (testCase) => {
  const expected = testCase?.expected_result ?? testCase?.expected_results;
  if (Array.isArray(expected)) return expected.join('\n');
  return String(expected || '');
};

const confidenceClass = (confidence) => {
  if (confidence == null) return 'border-slate-200 bg-slate-50 text-slate-600';
  if (confidence >= 0.85) return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (confidence >= 0.6) return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-rose-200 bg-rose-50 text-rose-700';
};

export const ExcelTestCaseImportStep = ({
  analysis,
  file,
  workspaceId,
  onBack,
  onPreview,
  onCommit,
}) => {
  const [mappingBySheet, setMappingBySheet] = React.useState(() => buildInitialMapping(analysis));
  const [selectedSheets, setSelectedSheets] = React.useState(() => (
    toArray(analysis?.sheets)
      .map((sheet) => String(sheet?.name || sheet?.sheet_name || '').trim())
      .filter(Boolean)
  ));
  const [sheetCategories, setSheetCategories] = React.useState(() => (
    buildInitialSheetCategories(analysis)
  ));
  const [sourceName, setSourceName] = React.useState(() => (
    String(file?.name || analysis?.filename || 'Imported Test Cases')
      .replace(/\.xlsx$/i, '')
      .trim()
  ));
  const [preview, setPreview] = React.useState(analysis || null);
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const [isCommitting, setIsCommitting] = React.useState(false);
  const [localError, setLocalError] = React.useState('');

  React.useEffect(() => {
    setMappingBySheet(buildInitialMapping(analysis));
    setSelectedSheets(
      toArray(analysis?.sheets)
        .map((sheet) => String(sheet?.name || sheet?.sheet_name || '').trim())
        .filter(Boolean)
    );
    setSheetCategories(buildInitialSheetCategories(analysis));
    setPreview(analysis || null);
    setSourceName(
      String(file?.name || analysis?.filename || 'Imported Test Cases')
        .replace(/\.xlsx$/i, '')
        .trim()
    );
    setLocalError('');
  }, [analysis, file]);

  const selectedSheetSet = React.useMemo(() => new Set(selectedSheets), [selectedSheets]);
  const activeSheets = toArray(analysis?.sheets).filter((sheet) => (
    selectedSheetSet.has(String(sheet?.name || sheet?.sheet_name || '').trim())
  ));
  const missingRequiredBySheet = activeSheets.reduce((acc, sheet) => {
    const sheetName = String(sheet?.name || sheet?.sheet_name || '').trim();
    const mapping = mappingBySheet[sheetName] || {};
    const missing = REQUIRED_FIELDS.filter((field) => !String(mapping[field] || '').trim());
    if (missing.length > 0) acc[sheetName] = missing;
    return acc;
  }, {});
  const canContinue = (
    selectedSheets.length > 0
    && Object.keys(missingRequiredBySheet).length === 0
    && String(sourceName || '').trim()
    && Boolean(preview)
    && !isPreviewing
    && !isCommitting
  );
  const displayedPayload = preview || analysis || {};
  const previewCases = resolveCases(displayedPayload);
  const diagnostics = collectDiagnostics(displayedPayload);
  const detectedCaseCount = resolveDetectedCaseCount(analysis);

  const updateHeaderMapping = (sheetName, sourceSelector, canonicalField) => {
    setMappingBySheet((previous) => {
      const nextSheetMapping = { ...(previous?.[sheetName] || {}) };
      Object.entries(nextSheetMapping).forEach(([key, value]) => {
        if (String(value) === String(sourceSelector)) delete nextSheetMapping[key];
      });
      if (canonicalField) {
        nextSheetMapping[canonicalField] = sourceSelector;
      }
      return {
        ...previous,
        [sheetName]: nextSheetMapping,
      };
    });
    setPreview(null);
  };

  const getSelectedCanonicalField = (sheetName, sourceSelector) => {
    const mapping = mappingBySheet[sheetName] || {};
    const entry = Object.entries(mapping).find(([, value]) => (
      String(value) === String(sourceSelector)
    ));
    return entry?.[0] || '';
  };

  const buildRequestPayload = () => ({
    application_id: workspaceId || undefined,
    mapping: mappingBySheet,
    sheet_names: selectedSheets,
    sheet_categories: sheetCategories,
  });

  const refreshPreview = async () => {
    if (!onPreview || selectedSheets.length === 0) return;
    setIsPreviewing(true);
    setPreview(null);
    setLocalError('');
    try {
      const payload = await onPreview(buildRequestPayload());
      setPreview(payload || null);
    } catch (error) {
      setLocalError(error?.message || 'Unable to build the import preview.');
    } finally {
      setIsPreviewing(false);
    }
  };

  const commitImport = async () => {
    if (!canContinue || !onCommit) return;
    setIsCommitting(true);
    setLocalError('');
    try {
      await onCommit({
        ...buildRequestPayload(),
        source_name: String(sourceName || '').trim(),
        section_title: String(sourceName || '').trim(),
        // The direct workflow edits one section at a time. Keep every selected
        // sheet in one review set so no imported sheet is hidden after commit.
        group_by: 'single',
      });
    } catch (error) {
      setLocalError(error?.message || 'Unable to create the testcase source.');
      setIsCommitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-cyan-50 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
              <FileSpreadsheet className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
                Excel testcase intake
              </p>
              <h3 className="mt-1 truncate text-xl font-bold text-slate-950">
                {file?.name || analysis?.filename || 'Workbook'}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Confirm how workbook columns map to testcase fields before creating the source.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-slate-700">
              {toArray(analysis?.sheets).length} sheet{toArray(analysis?.sheets).length === 1 ? '' : 's'}
            </span>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-slate-700">
              {detectedCaseCount} detected case{detectedCaseCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
          <div>
            <label htmlFor="excel-source-name" className="mb-1.5 block text-sm font-semibold text-slate-800">
              Source name
            </label>
            <input
              id="excel-source-name"
              className="text-input"
              value={sourceName}
              onChange={(event) => setSourceName(event.target.value)}
              placeholder="Imported Test Cases"
              maxLength={180}
            />
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Password-like test data is converted to protected runtime parameters and is not placed in generated scripts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {toArray(analysis?.sheets).map((sheet) => {
        const sheetName = String(sheet?.name || sheet?.sheet_name || '').trim();
        const headers = resolveSheetHeaders(sheet);
        const isSelected = selectedSheetSet.has(sheetName);
        const suggestions = sheet?.suggested_mapping || {};
        const missingFields = missingRequiredBySheet[sheetName] || [];
        return (
          <section key={sheetName} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex min-w-0 items-center gap-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(event) => {
                    setSelectedSheets((previous) => (
                      event.target.checked
                        ? Array.from(new Set([...previous, sheetName]))
                        : previous.filter((name) => name !== sheetName)
                    ));
                    setPreview(null);
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600"
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-slate-900">{sheetName}</span>
                  <span className="block text-xs text-slate-500">
                    Header row {Number(sheet?.header?.row || sheet?.header_row || 0) || 'not detected'}
                    {resolveUsedRange(sheet?.used_range) ? ` • ${resolveUsedRange(sheet.used_range)}` : ''}
                  </span>
                </span>
              </label>
              <select
                className="text-input min-w-[190px] bg-white text-sm"
                value={sheetCategories[sheetName] || 'uncategorized'}
                disabled={!isSelected}
                onChange={(event) => {
                  setSheetCategories((previous) => ({
                    ...previous,
                    [sheetName]: event.target.value,
                  }));
                  setPreview(null);
                }}
                aria-label={`Default category for ${sheetName}`}
              >
                <option value="uncategorized">Category not specified</option>
                <option value="positive">Positive cases</option>
                <option value="negative">Negative cases</option>
              </select>
            </div>

            {isSelected && (
              <div className="p-4">
                {headers.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2.5 font-semibold">Workbook column</th>
                          <th className="px-3 py-2.5 font-semibold">Import as</th>
                          <th className="px-3 py-2.5 font-semibold">Detection</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {headers.map((header) => {
                          const selectedCanonical = getSelectedCanonicalField(
                            sheetName,
                            header.selector
                          );
                          const suggestion = suggestions?.[selectedCanonical];
                          const suggestionSelector = resolveMappingSelector(suggestion);
                          const confidence = (
                            suggestionSelector === header.selector
                            || resolveMappingSelector({
                              source_header: suggestion?.source_header,
                            }) === header.label
                          )
                            ? resolveMappingConfidence(suggestion)
                            : null;
                          return (
                            <tr key={`${sheetName}-${header.key}`}>
                              <td className="max-w-[300px] px-3 py-2.5 font-medium text-slate-800">
                                <span className="block truncate" title={header.label}>
                                  {header.label}
                                  {header.column ? (
                                    <span className="ml-1.5 text-xs font-normal text-slate-400">
                                      ({header.column})
                                    </span>
                                  ) : null}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <select
                                  className="text-input min-w-[240px] bg-white text-sm"
                                  value={selectedCanonical}
                                  onChange={(event) => (
                                    updateHeaderMapping(
                                      sheetName,
                                      header.selector,
                                      event.target.value
                                    )
                                  )}
                                >
                                  <option value="">Do not map</option>
                                  {CANONICAL_FIELDS.map((field) => (
                                    <option key={field.key} value={field.key}>
                                      {field.label}{field.required ? ' *' : ''}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2.5">
                                {selectedCanonical ? (
                                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${confidenceClass(confidence)}`}>
                                    {confidence == null ? 'Confirmed mapping' : `${Math.round(confidence * 100)}% match`}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">Preserved as source data</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    No usable header row was detected for this sheet.
                  </div>
                )}

                {missingFields.length > 0 && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                      Map {missingFields.map((key) => (
                        CANONICAL_FIELDS.find((field) => field.key === key)?.label || key
                      )).join(', ')} before continuing.
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="font-semibold text-slate-900">Normalized preview</h4>
            <p className="mt-1 text-sm text-slate-600">
              Review how steps and expected results will enter the testcase editor.
            </p>
          </div>
          <button
            type="button"
            className="secondary-btn px-3 py-2 text-sm"
            onClick={refreshPreview}
            disabled={isPreviewing || selectedSheets.length === 0}
          >
            {isPreviewing ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {isPreviewing ? 'Refreshing...' : 'Refresh preview'}
          </button>
        </div>

        {previewCases.length > 0 ? (
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[900px] divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2.5">ID</th>
                  <th className="px-3 py-2.5">Description</th>
                  <th className="px-3 py-2.5">Steps</th>
                  <th className="px-3 py-2.5">Expected result</th>
                  <th className="px-3 py-2.5">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {previewCases.slice(0, 10).map((testCase, index) => {
                  const provenance = (
                    testCase?.source_provenance
                    || testCase?.provenance
                    || testCase?.source
                    || {}
                  );
                  return (
                    <tr key={`${testCase?.test_case_no || 'case'}-${index}`} className="align-top">
                      <td className="whitespace-nowrap px-3 py-3 font-semibold text-slate-800">
                        {testCase?.test_case_no || `Generated ${index + 1}`}
                      </td>
                      <td className="max-w-[260px] whitespace-pre-wrap px-3 py-3 text-slate-700">
                        {testCase?.test_description || testCase?.description || '—'}
                      </td>
                      <td className="max-w-[360px] whitespace-pre-wrap px-3 py-3 text-slate-700">
                        {caseStepsText(testCase) || '—'}
                      </td>
                      <td className="max-w-[300px] whitespace-pre-wrap px-3 py-3 text-slate-700">
                        {caseExpectedResultText(testCase) || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-500">
                        {provenance?.sheet || provenance?.sheet_name || testCase?.source_sheet || 'Sheet'}
                        {(provenance?.row || provenance?.source_row || testCase?.source_row)
                          ? ` row ${provenance?.row || provenance?.source_row || testCase?.source_row}`
                          : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-7 text-center text-sm text-slate-600">
            Refresh the preview after confirming the required mappings.
          </div>
        )}

        {previewCases.length > 10 && (
          <p className="mt-2 text-xs text-slate-500">
            Showing 10 of {previewCases.length} cases. All cases will be committed.
          </p>
        )}

        {diagnostics.length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-amber-900">Import checks</p>
            <ul className="mt-2 space-y-1 text-sm text-amber-800">
              {diagnostics.slice(0, 12).map((message) => (
                <li key={message} className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {localError && (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{localError}</span>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          className="secondary-btn h-12 w-full justify-center"
          onClick={onBack}
          disabled={isPreviewing || isCommitting}
        >
          <ArrowLeft className="h-4 w-4" />
          Choose another file
        </button>
        <button
          type="button"
          className="primary-btn h-12 w-full justify-center bg-gradient-to-r from-blue-600 to-cyan-600"
          onClick={commitImport}
          disabled={!canContinue}
          title={
            canContinue
              ? 'Create testcase source'
              : preview
                ? 'Complete required mappings first'
                : 'Refresh the normalized preview before continuing'
          }
        >
          {isCommitting ? (
            <Loader className="h-5 w-5 animate-spin" />
          ) : (
            <CheckCircle2 className="h-5 w-5" />
          )}
          {isCommitting ? 'Creating source...' : 'Create testcase source'}
          {!isCommitting && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};
